import { useViewportWidth } from '@redbubble/next/hooks';
import logger from '@redbubble/next/logger';
import { DeviceType } from '@redbubble/next/types';
import { useMemo } from 'react';
import { CategorySet, InventoryItem } from 'types/shop-bff/types';
import { findCompatibleInventoryItems } from '../pages/i/components/ProductConfigurator/attributeCompatibilityUtils';
import {
  AttributeName,
  ConfiguratorValues,
  DSFormOnChangeCallback,
  PRIMARY_ATTRIBUTE_NAMES,
} from '../pages/i/components/ProductConfigurator/types';
import {
  compareDisplayOrder,
  configuratorValuesToFormikValues,
  filterConfigurationAttributeAvailableOptions,
  formikValuesToConfiguratorValues,
  isAttributeInAttributeContainer,
  parseAttributesFromCategorySet,
} from '../pages/i/components/ProductConfigurator/utils';

export const useProductConfiguratorForm = ({
  currentInventoryItem,
  inventoryCategorySet,
  setCurrentInventoryItem,
}: {
  currentInventoryItem: InventoryItem;
  inventoryCategorySet: CategorySet;
  setCurrentInventoryItem: (inventoryItem: InventoryItem) => void;
}) => {
  const compatibleInventoryItems = useMemo(
    () =>
      findCompatibleInventoryItems(
        currentInventoryItem,
        inventoryCategorySet.items
      ),
    [currentInventoryItem, inventoryCategorySet]
  );

  const configuratorFields = useMemo(() => {
    const allOptions = parseAttributesFromCategorySet(
      inventoryCategorySet,
      currentInventoryItem
    );

    return allOptions
      .map((option) =>
        filterConfigurationAttributeAvailableOptions(
          option,
          compatibleInventoryItems
        )
      )
      .filter((fieldOption) => fieldOption.options.length !== 0)
      .sort(compareDisplayOrder);
  }, [compatibleInventoryItems, inventoryCategorySet, currentInventoryItem]);

  const onConfiguratorChange: (newValue: ConfiguratorValues) => void = (
    values
  ) => {
    const matchedInventoryItems = inventoryCategorySet.items.filter((item) =>
      values.every((value) => isAttributeInAttributeContainer(value, item))
    );

    if (matchedInventoryItems.length === 0) {
      const fallbackInventoryItem = (() => {
        const primaryConfigValueChanged = values.find((v) =>
          PRIMARY_ATTRIBUTE_NAMES.includes(v.name)
        );

        if (primaryConfigValueChanged) {
          return inventoryCategorySet.items.find((item) =>
            item.attributes.some(
              (a) =>
                a.name === primaryConfigValueChanged.name &&
                a.value === primaryConfigValueChanged.value
            )
          );
        }
        return null;
      })();

      if (fallbackInventoryItem) {
        setCurrentInventoryItem(fallbackInventoryItem);
      } else {
        // TODO DEP[1931]: We need to provide better feedback to users if an error has occurred during their action https://redbubble.atlassian.net/browse/DEP-2197
        const err = {
          message:
            'Product configurator could resolved no inventory item or fallback item',
        };
        logger.error(
          {
            err,
            inventoryCategorySet,
            message: err.message,
            context: 'useProductConfiguratorForm',
          },
          err.message
        );
        setCurrentInventoryItem(currentInventoryItem);
      }

      return;
    }

    // TODO DEP[1931]: We need to provide better feedback to users if an error has occurred during their action https://redbubble.atlassian.net/browse/DEP-2197
    if (matchedInventoryItems.length > 1) {
      const err = {
        message:
          'Product configurator resolved more than one inventory item for the requested configuration',
      };
      logger.error(
        {
          err,
          inventoryCategorySet,
          message: err.message,
          context: 'useProductConfiguratorForm',
        },
        err.message
      );

      setCurrentInventoryItem(matchedInventoryItems[0]);
      return;
    }

    setCurrentInventoryItem(matchedInventoryItems[0]);
  };

  const defaultValues = useMemo(
    () => configuratorValuesToFormikValues(currentInventoryItem.attributes),
    [currentInventoryItem.attributes]
  );

  const onConfiguratorFormChange: DSFormOnChangeCallback = (formState) => {
    if (Object.keys(defaultValues) === Object.keys(formState.values)) {
      onConfiguratorChange(
        formikValuesToConfiguratorValues(configuratorFields, formState.values)
      );
    } else {
      onConfiguratorChange(
        formikValuesToConfiguratorValues(configuratorFields, {
          ...defaultValues,
          ...formState.values,
        })
      );
    }
  };

  const { isOrAbove } = useViewportWidth();
  const isMobile = !isOrAbove(DeviceType.Tablet);
  const hasColorConfiguratorField = configuratorFields.some(
    (field) =>
      field.name === AttributeName.BodyColor ||
      field.name === AttributeName.Color
  );

  const showColorConfigSeparately = isMobile && hasColorConfiguratorField;

  return {
    configuratorFields,
    onConfiguratorFormChange,
    showColorConfigSeparately,
  };
};
